import { ItemChapter, ItemPage } from '@orientaction/components';
import { useEffect, useMemo, useState } from 'react';
import { UseEditEbookReturn } from '../hooks/useEditEbook';
import { useEditSidebarStyles } from './EditSidebar.styles';
import { EbookContentBlock } from '../types';

type EditSidebarProps = Pick<
  UseEditEbookReturn,
  | 'allBlocks'
  | 'coverUrl'
  | 'currentTitleIndex'
  | 'history'
  | 'scrollToSection'
  | 'selectBlock'
  | 'setBlockSelected'
  | 'setCurrentTitleIndex'
  | 'setIndexSelected'
  | 'setValueInput'
  | 'valuesEbooksTemplate'
>;

type ChapterGroup = {
  chapterIndex: number;
  chapter: EbookContentBlock;
  subtitleIndices: number[];
};

const buildChapterGroups = (allBlocks: EbookContentBlock[]): ChapterGroup[] => {
  const groups: ChapterGroup[] = [];
  let currentGroup: ChapterGroup | null = null;

  allBlocks.forEach((block, index) => {
    if (block?.bloc_template !== 2) return;

    if (block.title_type === 'subtitle') {
      currentGroup?.subtitleIndices.push(index);
      return;
    }

    currentGroup = {
      chapterIndex: index,
      chapter: block,
      subtitleIndices: [],
    };
    groups.push(currentGroup);
  });

  return groups;
};

const EditSidebar = ({
  allBlocks,
  coverUrl,
  currentTitleIndex,
  history,
  scrollToSection,
  setBlockSelected,
  setCurrentTitleIndex,
  setIndexSelected,
  setValueInput,
  valuesEbooksTemplate,
}: EditSidebarProps) => {
  const classes = useEditSidebarStyles();
  const chapterGroups = useMemo(() => buildChapterGroups(allBlocks), [allBlocks]);
  const [expandedChapters, setExpandedChapters] = useState<Record<number, boolean>>({});

  const isChapterExpanded = (chapterIndex: number) => expandedChapters[chapterIndex] ?? true;

  const toggleChapter = (chapterIndex: number) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterIndex]: !isChapterExpanded(chapterIndex),
    }));
  };

  const selectChapterItem = (index: number, block: EbookContentBlock) => {
    setIndexSelected(index);
    setBlockSelected(block);
    setValueInput(index + 1);
    setCurrentTitleIndex(index);
    scrollToSection(index);
  };

  useEffect(() => {
    const parentGroup = chapterGroups.find(
      group =>
        group.chapterIndex === currentTitleIndex ||
        group.subtitleIndices.includes(currentTitleIndex),
    );

    if (!parentGroup || parentGroup.subtitleIndices.length === 0) return;

    setExpandedChapters(prev => {
      if (prev[parentGroup.chapterIndex] ?? true) return prev;

      return {
        ...prev,
        [parentGroup.chapterIndex]: true,
      };
    });
  }, [chapterGroups, currentTitleIndex]);

  return (
    <aside className={classes.chapterSidebar} aria-label="Sommaire contextuel">
      <ItemPage
        id="1"
        title={valuesEbooksTemplate?.title || 'titre du ebook'}
        url={coverUrl}
        isCover={true}
        handleClick={() => history.push('/admin/ebook/edit-main/')}
      />
      <div className={classes.chapterList}>
        {chapterGroups.map(({ chapterIndex, chapter, subtitleIndices }) => {
          const hasSubChapters = subtitleIndices.length > 0;
          const isExpanded = isChapterExpanded(chapterIndex);

          return (
            <div key={`chapter-group-${chapterIndex}`}>
              <ItemChapter
                id={chapterIndex as any}
                title={chapter.title || ''}
                isSelected={chapterIndex === currentTitleIndex}
                hasSubChapters={hasSubChapters}
                isExpanded={isExpanded}
                onToggleExpand={() => toggleChapter(chapterIndex)}
                handleClick={() => selectChapterItem(chapterIndex, chapter)}
              />

              {hasSubChapters &&
                isExpanded &&
                subtitleIndices.map(subtitleIndex => {
                  const subtitle = allBlocks[subtitleIndex];

                  return (
                    <ItemChapter
                      key={`subtitle-${subtitleIndex}`}
                      id={subtitleIndex as any}
                      title={subtitle.title || ''}
                      isSubChapter={true}
                      isSelected={subtitleIndex === currentTitleIndex}
                      handleClick={() => selectChapterItem(subtitleIndex, subtitle)}
                    />
                  );
                })}
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export default EditSidebar;
